import { BaseService } from "../../core/baseService";
import {
  UpsellServiceInterface,
  MenuItemSuggestion,
  CategorySuggestion,
  TriggerTypeOption,
  SuggestionTypeOption,
  TriggerConditionOption,
  GetMenuItemSuggestionsParams,
  GetCategorySuggestionsParams,
  CreateMenuItemSuggestionParams,
  UpdateMenuItemSuggestionParams,
  DeleteMenuItemSuggestionParams,
  CreateCategorySuggestionParams,
  UpdateCategorySuggestionParams,
  DeleteCategorySuggestionParams,
  AiUpsellSuggestion,
  GetAiSuggestionsParams,
  GenerateAiSuggestionsRequest,
  GenerateAiSuggestionsResponse,
  EnableAiSuggestionRequest,
  AiSuggestionActionResponse,
} from "../types/upsell.types";
import { ApiResult, PaginatedResponse } from "../../core/types";

export class UpsellService
  extends BaseService
  implements UpsellServiceInterface
{
  // Menu Item Suggestions

  async getMenuItemSuggestions(
    params?: GetMenuItemSuggestionsParams,
  ): Promise<ApiResult<PaginatedResponse<MenuItemSuggestion>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.searchTerm) queryParams.append("searchTerm", params.searchTerm);
    if (params?.suggestionType)
      queryParams.append("suggestionType", params.suggestionType);
    if (params?.isActive !== undefined)
      queryParams.append("isActive", params.isActive.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/upsell/item-suggestions?${queryString}`
      : "/api/admin/upsell/item-suggestions";

    return this.handleRequest<PaginatedResponse<MenuItemSuggestion>>(
      this.client.get(url),
    );
  }

  async getAllMenuItemSuggestions(
    includeInactive = false,
  ): Promise<ApiResult<MenuItemSuggestion[]>> {
    return this.handleRequest<MenuItemSuggestion[]>(
      this.client.get(
        `/api/admin/upsell/item-suggestions/all?includeInactive=${includeInactive}`,
      ),
    );
  }

  async getMenuItemSuggestion(
    suggestionId: string,
  ): Promise<ApiResult<MenuItemSuggestion>> {
    return this.handleRequest<MenuItemSuggestion>(
      this.client.get(`/api/admin/upsell/item-suggestions/${suggestionId}`),
    );
  }

  async createMenuItemSuggestion(
    params: CreateMenuItemSuggestionParams,
  ): Promise<ApiResult<MenuItemSuggestion>> {
    return this.handleRequest<MenuItemSuggestion>(
      this.client.post("/api/admin/upsell/item-suggestions", params),
    );
  }

  async updateMenuItemSuggestion(
    params: UpdateMenuItemSuggestionParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(
        `/api/admin/upsell/item-suggestions/${params.suggestionId}`,
        params.data,
      ),
    );
  }

  async deleteMenuItemSuggestion(
    params: DeleteMenuItemSuggestionParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(
        `/api/admin/upsell/item-suggestions/${params.suggestionId}`,
      ),
    );
  }

  // Category Suggestions

  async getCategorySuggestions(
    params?: GetCategorySuggestionsParams,
  ): Promise<ApiResult<PaginatedResponse<CategorySuggestion>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.searchTerm) queryParams.append("searchTerm", params.searchTerm);
    if (params?.isActive !== undefined)
      queryParams.append("isActive", params.isActive.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/upsell/category-suggestions?${queryString}`
      : "/api/admin/upsell/category-suggestions";

    return this.handleRequest<PaginatedResponse<CategorySuggestion>>(
      this.client.get(url),
    );
  }

  async getAllCategorySuggestions(
    includeInactive = false,
  ): Promise<ApiResult<CategorySuggestion[]>> {
    return this.handleRequest<CategorySuggestion[]>(
      this.client.get(
        `/api/admin/upsell/category-suggestions/all?includeInactive=${includeInactive}`,
      ),
    );
  }

  async getCategorySuggestion(
    suggestionId: string,
  ): Promise<ApiResult<CategorySuggestion>> {
    return this.handleRequest<CategorySuggestion>(
      this.client.get(`/api/admin/upsell/category-suggestions/${suggestionId}`),
    );
  }

  async createCategorySuggestion(
    params: CreateCategorySuggestionParams,
  ): Promise<ApiResult<CategorySuggestion>> {
    return this.handleRequest<CategorySuggestion>(
      this.client.post("/api/admin/upsell/category-suggestions", params),
    );
  }

  async updateCategorySuggestion(
    params: UpdateCategorySuggestionParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(
        `/api/admin/upsell/category-suggestions/${params.suggestionId}`,
        params.data,
      ),
    );
  }

  async deleteCategorySuggestion(
    params: DeleteCategorySuggestionParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(
        `/api/admin/upsell/category-suggestions/${params.suggestionId}`,
      ),
    );
  }

  // Lookups

  async getTriggerTypes(): Promise<ApiResult<TriggerTypeOption[]>> {
    return this.handleRequest<TriggerTypeOption[]>(
      this.client.get("/api/admin/upsell/trigger-types"),
    );
  }

  async getSuggestionTypes(): Promise<ApiResult<SuggestionTypeOption[]>> {
    return this.handleRequest<SuggestionTypeOption[]>(
      this.client.get("/api/admin/upsell/suggestion-types"),
    );
  }

  async getTriggerConditions(): Promise<ApiResult<TriggerConditionOption[]>> {
    return this.handleRequest<TriggerConditionOption[]>(
      this.client.get("/api/admin/upsell/trigger-conditions"),
    );
  }

  // AI Suggestions

  async getAiSuggestions(
    params?: GetAiSuggestionsParams,
  ): Promise<ApiResult<AiUpsellSuggestion[]>> {
    const queryParams = new URLSearchParams();
    if (params?.includeEnabled !== undefined)
      queryParams.append("includeEnabled", params.includeEnabled.toString());
    if (params?.includeDismissed !== undefined)
      queryParams.append("includeDismissed", params.includeDismissed.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/upsell/ai-suggestions?${queryString}`
      : "/api/admin/upsell/ai-suggestions";

    return this.handleRequest<AiUpsellSuggestion[]>(this.client.get(url));
  }

  async getAiSuggestion(
    suggestionId: string,
  ): Promise<ApiResult<AiUpsellSuggestion>> {
    return this.handleRequest<AiUpsellSuggestion>(
      this.client.get(`/api/admin/upsell/ai-suggestions/${suggestionId}`),
    );
  }

  async generateAiSuggestions(
    request: GenerateAiSuggestionsRequest,
  ): Promise<ApiResult<GenerateAiSuggestionsResponse>> {
    return this.handleRequest<GenerateAiSuggestionsResponse>(
      this.client.post("/api/admin/upsell/ai-suggestions/generate", request),
    );
  }

  async enableAiSuggestion(
    suggestionId: string,
    request: EnableAiSuggestionRequest,
  ): Promise<ApiResult<AiSuggestionActionResponse>> {
    return this.handleRequest<AiSuggestionActionResponse>(
      this.client.post(
        `/api/admin/upsell/ai-suggestions/${suggestionId}/enable`,
        request,
      ),
    );
  }

  async dismissAiSuggestion(suggestionId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(
        `/api/admin/upsell/ai-suggestions/${suggestionId}/dismiss`,
      ),
    );
  }

  async deleteAiSuggestion(suggestionId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/upsell/ai-suggestions/${suggestionId}`),
    );
  }

  async refreshAiSuggestions(
    request: GenerateAiSuggestionsRequest,
  ): Promise<ApiResult<{ suggestionsGenerated: number }>> {
    return this.handleRequest<{ suggestionsGenerated: number }>(
      this.client.post("/api/admin/upsell/ai-suggestions/refresh", request),
    );
  }
}
